import { Alert, Box, Link, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import type { ActiveTenantReportSummary } from '../../types';
import { formatDateTime } from '../../utils/date';


interface ActiveTenantReportAlertProps {
  report: ActiveTenantReportSummary;
  locale?: string;
}

/**
 * Show the tenant-wide report generation lock summary reused by report modals.
 */
export default function ActiveTenantReportAlert({
  report,
  locale = 'en',
}: ActiveTenantReportAlertProps) {
  const { t } = useTranslation(['pages']);
  const reportTypeLabel = report.reportType === 'campaign'
    ? t('pages:reports.generationLock.types.campaign')
    : report.reportType === 'call'
      ? t('pages:reports.generationLock.types.call')
      : t('pages:reports.generationLock.types.user_activity');

  const rows: Array<{ key: string; label: string; value: string }> = [
    {
      key: 'type',
      label: t('pages:reports.generationLock.fields.type'),
      value: reportTypeLabel,
    },
    {
      key: 'user',
      label: t('pages:reports.generationLock.fields.user'),
      value: report.createdByName || t('pages:reports.generationLock.unknownUser'),
    },
  ];

  if (report.startedAt) {
    rows.push({
      key: 'startedAt',
      label: t('pages:reports.generationLock.fields.startedAt'),
      value: formatDateTime(report.startedAt, { locale }),
    });
  }
  if (report.campaignName) {
    rows.push({
      key: 'campaign',
      label: t('pages:reports.generationLock.fields.campaign'),
      value: report.campaignName,
    });
  }
  if (report.name) {
    rows.push({
      key: 'name',
      label: t('pages:reports.generationLock.fields.name'),
      value: report.name,
    });
  }

  return (
    <Alert severity="info">
      <Typography variant="body2" sx={{ mb: 1 }}>
        {t('pages:reports.generationLock.messagePrefix')}
        <Link
          component={RouterLink}
          to={report.reportsPath}
          color="inherit"
          underline="always"
          sx={{ fontWeight: 700 }}
        >
          {t('pages:reports.generationLock.messageLink')}
        </Link>
        {t('pages:reports.generationLock.messageSuffix')}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
        {rows.map((row) => (
          <Typography key={row.key} variant="caption" color="inherit">
            <strong>{row.label}:</strong> {row.value}
          </Typography>
        ))}
      </Box>
    </Alert>
  );
}
